import React, { useState, useEffect } from "react";
import { TOKENS } from "../tokens";
import { Card } from "../components/Card";
import { SectionLabel } from "../components/SectionLabel";
import { useApp } from "../context/AppContext";
import { api } from "../api/client";
import { Calculator, FileText, Download, Users, ChevronLeft, Search, TableProperties, Edit, Trash2 } from "lucide-react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import autoTable from "jspdf-autotable";

export function ManagerPayrollScreen() {
  const { team } = useApp();
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    casualLeave: 0,
    lopDays: 0,
    actualPay: "",
    basicPay: "",
    incentive: "",
    includePF: true,
    includeESI: true,
    includeTDS: true,
  });

const [calculation, setCalculation] = useState(null);
  
  const [payrollHistory, setPayrollHistory] = useState(() => JSON.parse(localStorage.getItem('wibil_payroll_history') || '{}'));
  const [isIssued, setIsIssued] = useState(false);

  useEffect(() => {
    if (selectedEmp) {
      const empId = selectedEmp.id || selectedEmp.employee_id || selectedEmp.employeeId;
      const historyRecord = payrollHistory[formData.year]?.[formData.month]?.[empId];
      if (historyRecord) {
        setFormData(historyRecord.formData);
        setCalculation(historyRecord.calculation);
        setIsIssued(true);
      } else {
        setIsIssued(false);
        const pay = selectedEmp.baseSalary || 0;
        setFormData(prev => ({
          ...prev,
          actualPay: pay || "",
          basicPay: pay ? (pay * 0.55).toFixed(2) : "",
          casualLeave: 0,
          lopDays: 0,
          incentive: "",
          includePF: true,
          includeESI: true,
          includeTDS: true,
        }));
      }
    }
  }, [selectedEmp, formData.month, formData.year]); // Depend on month/year but avoid formData object reference

  const [listMonth, setListMonth] = useState(new Date().getMonth() + 1);
  const [listYear, setListYear] = useState(new Date().getFullYear());

  const exportExcel = () => {
    const records = payrollHistory[listYear]?.[listMonth];
    if (!records || Object.keys(records).length === 0) {
      alert("No payroll records found for this month.");
      return;
    }

    const data = [];
    Object.keys(records).forEach(empId => {
      const rec = records[empId];
      data.push({
        "Employee ID": empId,
        "Employee Name": rec.empName || "Unknown",
        "Role": rec.empRole || "",
        "Actual Pay": parseFloat(rec.formData.actualPay).toFixed(2),
        "Total Days": rec.calculation?.totalDaysInMonth || 30,
        "Paid Days": rec.calculation?.totalPaidDays || 30,
        "LOP": rec.calculation?.lopDays || 0,
        "Incentive": rec.calculation?.incentiveAmount?.toFixed(2) || "0.00",
        "Total Deductions": rec.calculation.totalDeductions.toFixed(2),
        "Net Amount": rec.calculation.netPay.toFixed(2)
      });
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Salary Transfer");
    XLSX.writeFile(workbook, `Salary_Transfer_${listMonth}_${listYear}.xlsx`);
  };

  useEffect(() => {
    if (formData.actualPay) {
      const actual = parseFloat(formData.actualPay) || 0;
      const basic = parseFloat(formData.basicPay) || (actual * 0.55);
      const casualLeave = parseFloat(formData.casualLeave) || 0;
      const incentiveAmount = parseFloat(formData.incentive) || 0;
      const explicitLop = parseFloat(formData.lopDays) || 0;
      
      const totalDaysInMonth = new Date(formData.year, formData.month, 0).getDate();
      const totalPaidDays = Math.max(0, totalDaysInMonth - explicitLop);
      const ratio = totalPaidDays / totalDaysInMonth;

      const proratedActual = actual * ratio;
      const proratedBasic = basic * ratio;
      const allowances = proratedActual - proratedBasic;

      const pfEmployee = (formData.includePF ?? true) ? proratedBasic * 0.12 : 0;
      const pfEmployer = (formData.includePF ?? true) ? proratedBasic * 0.12 : 0;
      const esiEmployee = (formData.includeESI ?? true) ? proratedActual * 0.0075 : 0;
      const esiEmployer = (formData.includeESI ?? true) ? proratedActual * 0.0325 : 0;
      
      // TDS - 10% if actual pay is above 100,000
      let tds = 0;
      if ((formData.includeTDS ?? true) && actual > 100000) {
        tds = proratedActual * 0.10;
      }

      const totalDeductions = pfEmployee + esiEmployee + tds;
      const netPay = proratedActual - totalDeductions + incentiveAmount;
      const totalEarningsDisplay = proratedActual + incentiveAmount;
      const lopDays = explicitLop;

      setCalculation({
        totalDaysInMonth,
        proratedActual,
        proratedBasic,
        allowances,
        pfEmployee,
        pfEmployer,
        esiEmployee,
        esiEmployer,
        tds,
        totalDeductions,
        netPay,
        lopDays,
        incentiveAmount,
        totalEarningsDisplay,
        totalPaidDays
      });
    } else {
      setCalculation(null);
    }
  }, [formData]);

  const handleActualPayChange = (e) => {
    const val = e.target.value;
    setFormData(prev => ({
      ...prev,
      actualPay: val,
      basicPay: val ? (parseFloat(val) * 0.55).toFixed(2) : ""
    }));
  };

  
const numberToWords = (num) => {
  const a = ['','One ','Two ','Three ','Four ', 'Five ','Six ','Seven ','Eight ','Nine ','Ten ','Eleven ','Twelve ','Thirteen ','Fourteen ','Fifteen ','Sixteen ','Seventeen ','Eighteen ','Nineteen '];
  const b = ['', '', 'Twenty','Thirty','Forty','Fifty', 'Sixty','Seventy','Eighty','Ninety'];
  if ((num = num.toString()).length > 9) return 'overflow';
  let n = ('000000000' + num).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
  if (!n) return '';
  let str = '';
  str += (n[1] != 0) ? (a[Number(n[1])] || b[n[1][0]] + ' ' + a[n[1][1]]) + 'Crore ' : '';
  str += (n[2] != 0) ? (a[Number(n[2])] || b[n[2][0]] + ' ' + a[n[2][1]]) + 'Lakh ' : '';
  str += (n[3] != 0) ? (a[Number(n[3])] || b[n[3][0]] + ' ' + a[n[3][1]]) + 'Thousand ' : '';
  str += (n[4] != 0) ? (a[Number(n[4])] || b[n[4][0]] + ' ' + a[n[4][1]]) + 'Hundred ' : '';
  str += (n[5] != 0) ? ((str != '') ? 'and ' : '') + (a[Number(n[5])] || b[n[5][0]] + ' ' + a[n[5][1]]) : '';
  return str.trim();
};

const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthStr = monthNames[formData.month - 1];


    const handleDeletePayslip = () => {
      if (!window.confirm("Are you sure you want to delete this issued payslip?")) return;
      const empId = selectedEmp.id || selectedEmp.employee_id || selectedEmp.employeeId;
      const newHistory = { ...payrollHistory };
      if (newHistory[formData.year] && newHistory[formData.year][formData.month]) {
        delete newHistory[formData.year][formData.month][empId];
      }
      setPayrollHistory(newHistory);
      localStorage.setItem('wibil_payroll_history', JSON.stringify(newHistory));
      setIsIssued(false);
      // Reset to defaults
      const pay = selectedEmp.baseSalary || 0;
      setFormData(prev => ({
        ...prev,
        actualPay: pay || "",
        basicPay: pay ? (pay * 0.55).toFixed(2) : "",
        workingDays: 30,
        casualLeave: 0,
        includePF: true,
        includeESI: true,
        includeTDS: true,
      }));
    };

    const generatePDF = async () => {
      if (!calculation || !selectedEmp) return;

      const element = document.getElementById('payslip-preview-content');
      if (!element) return;

      try {
        const canvas = await html2canvas(element, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
        const imgData = canvas.toDataURL('image/png');
        
        const pdf = new jsPDF({ orientation: "portrait", format: "a4" });
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
        
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
        
        const filename = `Payslip_${selectedEmp.name.replace(/ /g, '_')}_${formData.month}_${formData.year}.pdf`;
        const pdfBlob = pdf.output("blob");

        // Save locally for the manager
        pdf.save(filename);

        // Upload to backend
        const fd = new FormData();
        fd.append("file", pdfBlob, filename);
        fd.append("caption", `Payslip - ${monthStr}/${formData.year}`);

        const empId = selectedEmp.employee_id || selectedEmp.id;
        await api.post(`/employees/${empId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });

        alert("Payslip PDF generated and added to employee portal successfully!");

        // Update history if not already issued
        if (!isIssued) {
          const empIdToSave = selectedEmp.id || selectedEmp.employee_id || selectedEmp.employeeId;
          const newHistory = { ...payrollHistory };
          if (!newHistory[formData.year]) newHistory[formData.year] = {};
          if (!newHistory[formData.year][formData.month]) newHistory[formData.year][formData.month] = {};
          
          newHistory[formData.year][formData.month][empIdToSave] = {
            empName: selectedEmp.name,
            empRole: selectedEmp.role,
            formData: formData,
            calculation: calculation,
            issuedAt: new Date().toISOString()
          };
          setPayrollHistory(newHistory);
          localStorage.setItem('wibil_payroll_history', JSON.stringify(newHistory));
          setIsIssued(true);
        }
      } catch (err) {
        console.error("Error saving payslip:", err);
        alert("Payslip downloaded, but failed to upload to employee portal.");
      }
    };


  if (!selectedEmp) {
    const emps = team.filter(e => 
      !e.is_disabled && e.role !== "manager" && e.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const approvedRecords = payrollHistory[listYear]?.[listMonth] || {};
    const approvedEmps = emps.filter(e => approvedRecords[e.id || e.employee_id || e.employeeId]);
    const pendingEmps = emps.filter(e => !approvedRecords[e.id || e.employee_id || e.employeeId]);

    return (
      <div style={{ paddingBottom: 40 }}>
        <SectionLabel>Payroll Generation & Salary Transfer</SectionLabel>
        
        <Card style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, display: "block", marginBottom: 4 }}>Month</label>
              <select
                value={listMonth}
                onChange={e => setListMonth(parseInt(e.target.value))}
                style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1.5px solid ${TOKENS.border}`, fontSize: 13, outline: "none" }}
              >
                {monthNames.map((m, i) => (
                  <option key={m} value={i + 1}>{m}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, display: "block", marginBottom: 4 }}>Year</label>
              <select
                value={listYear}
                onChange={e => setListYear(parseInt(e.target.value))}
                style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1.5px solid ${TOKENS.border}`, fontSize: 13, outline: "none" }}
              >
                {[new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <button
              onClick={exportExcel}
              style={{ padding: "10px 16px", borderRadius: 8, background: TOKENS.gold, color: TOKENS.navyDeep, border: "none", fontWeight: 700, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}
            >
              <Download size={16} /> Salary Transfer Excel
            </button>
          </div>
        </Card>
        
        <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
          <div style={{ flex: 1, position: "relative" }}>
            <Search size={16} color={TOKENS.muted} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
            <input
              placeholder="Search by name..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{
                width: "100%", padding: "10px 10px 10px 36px", borderRadius: 12,
                border: `1.5px solid ${TOKENS.border}`, fontSize: 13, color: TOKENS.ink, outline: "none"
              }}
            />
          </div>
        </div>

        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: TOKENS.muted, marginBottom: 10, paddingLeft: 4 }}>PENDING PAYROLL ({pendingEmps.length})</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {pendingEmps.map(e => (
              <Card key={e.id} style={{ padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }} onClick={() => {
                setSelectedEmp(e);
                setFormData(prev => ({...prev, month: listMonth, year: listYear}));
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 40, height: 40, borderRadius: "50%", background: `${TOKENS.navyDeep}15`, color: TOKENS.navyDeep, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>
                    {e.initials}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: TOKENS.ink }}>{e.name}</div>
                    <div style={{ fontSize: 11, color: TOKENS.muted }}>{e.role}</div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, color: TOKENS.gold, fontWeight: 700, fontSize: 12 }}>
                  Generate <ChevronLeft size={16} style={{ transform: "rotate(180deg)" }} />
                </div>
              </Card>
            ))}
            {pendingEmps.length === 0 && (
              <div style={{ textAlign: "center", padding: 20, color: TOKENS.muted, fontSize: 13, background: "#fff", borderRadius: 12 }}>All employees have approved payslips for this month!</div>
            )}
          </div>
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: TOKENS.muted, marginBottom: 10, paddingLeft: 4 }}>APPROVED PAYROLL ({approvedEmps.length})</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {approvedEmps.map(e => {
              const rec = approvedRecords[e.id || e.employee_id || e.employeeId];
              return (
                <Card key={e.id} style={{ padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", borderLeft: `4px solid #10b981` }} onClick={() => {
                  setSelectedEmp(e);
                  setFormData(prev => ({...prev, month: listMonth, year: listYear}));
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: "50%", background: `#10b98115`, color: "#10b981", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>
                      {e.initials}
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: TOKENS.ink }}>{e.name}</div>
                      <div style={{ fontSize: 11, color: "#10b981", fontWeight: 700 }}>Net Pay: INR {rec.calculation.netPay.toFixed(2)}</div>
                    </div>
                  </div>
                  <ChevronLeft size={16} color={TOKENS.muted} style={{ transform: "rotate(180deg)" }} />
                </Card>
              );
            })}
            {approvedEmps.length === 0 && (
              <div style={{ textAlign: "center", padding: 20, color: TOKENS.muted, fontSize: 13, background: "#fff", borderRadius: 12 }}>No approved payslips for this month yet.</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: 40 }}>
      <button onClick={() => setSelectedEmp(null)} style={{ display: "flex", alignItems: "center", gap: 6, border: "none", background: "none", color: TOKENS.navyDeep, fontSize: 13, fontWeight: 700, marginBottom: 16, cursor: "pointer" }}>
        <ChevronLeft size={16} /> Back to List
      </button>

      <Card style={{ padding: 16, marginBottom: 16, background: `linear-gradient(135deg, ${TOKENS.navyDeep}, ${TOKENS.navySoft})`, color: "#fff" }}>
        <div style={{ fontSize: 16, fontWeight: 700 }}>Payroll Generation</div>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)" }}>For {selectedEmp.name}</div>
      </Card>

      <SectionLabel>Payroll Input Details</SectionLabel>
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, display: "block", marginBottom: 4 }}>Month</label>
            <select
              value={formData.month}
              onChange={e => setFormData(f => ({...f, month: parseInt(e.target.value)}))}
              style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1.5px solid ${TOKENS.border}`, fontSize: 13, outline: "none", background: "#fff" }}
            >
              {Array.from({length: 12}, (_, i) => i + 1).map(m => (
                <option key={m} value={m}>{new Date(0, m - 1).toLocaleString('default', { month: 'long' })}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, display: "block", marginBottom: 4 }}>Year</label>
            <input
              type="number"
              value={formData.year}
              onChange={e => setFormData(f => ({...f, year: parseInt(e.target.value)}))}
              style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1.5px solid ${TOKENS.border}`, fontSize: 13, outline: "none" }}
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, display: "block", marginBottom: 4 }}>Actual Pay (Total Salary)</label>
            <input
              type="number"
              placeholder="Enter current pay"
              value={formData.actualPay}
              onChange={handleActualPayChange} disabled={isIssued}
              style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1.5px solid ${TOKENS.border}`, fontSize: 13, outline: "none" }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, display: "block", marginBottom: 4 }}>Basic Pay (55%)</label>
            <input
              type="number"
              value={formData.basicPay}
              onChange={e => setFormData(f => ({...f, basicPay: e.target.value}))} disabled={isIssued}
              style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1.5px solid ${TOKENS.border}`, fontSize: 13, outline: "none" }}
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, display: "block", marginBottom: 4 }}>Working Days</label>
            <input
              type="number"
              value={new Date(formData.year, formData.month, 0).getDate()}
              disabled={true}
              style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1.5px solid ${TOKENS.border}`, fontSize: 13, outline: "none", backgroundColor: "#f5f5f5" }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, display: "block", marginBottom: 4 }}>Casual Leave</label>
            <input
              type="number"
              value={formData.casualLeave}
              onChange={e => setFormData(f => ({...f, casualLeave: e.target.value}))} disabled={isIssued}
              style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1.5px solid ${TOKENS.border}`, fontSize: 13, outline: "none" }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, display: "block", marginBottom: 4 }}>LOP (Days)</label>
            <input
              type="number"
              value={formData.lopDays || ""}
              onChange={e => setFormData(f => ({...f, lopDays: e.target.value}))} disabled={isIssued}
              style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1.5px solid ${TOKENS.border}`, fontSize: 13, outline: "none" }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, display: "block", marginBottom: 4 }}>Incentive / Bonus</label>
            <input
              type="number"
              value={formData.incentive}
              onChange={e => setFormData(f => ({...f, incentive: e.target.value}))} disabled={isIssued}
              style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1.5px solid ${TOKENS.border}`, fontSize: 13, outline: "none" }}
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: 16, marginBottom: 12, marginTop: 16 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: TOKENS.ink, fontWeight: 600 }}>
            <input 
              type="checkbox" 
              checked={formData.includePF ?? true} 
              onChange={e => setFormData(f => ({...f, includePF: e.target.checked}))} 
              disabled={isIssued}
              style={{ width: 16, height: 16, accentColor: TOKENS.navyDeep }}
            />
            Include PF (12%)
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: TOKENS.ink, fontWeight: 600 }}>
            <input 
              type="checkbox" 
              checked={formData.includeESI ?? true} 
              onChange={e => setFormData(f => ({...f, includeESI: e.target.checked}))} 
              disabled={isIssued}
              style={{ width: 16, height: 16, accentColor: TOKENS.navyDeep }}
            />
            Include ESI (0.75%)
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: TOKENS.ink, fontWeight: 600 }}>
            <input 
              type="checkbox" 
              checked={formData.includeTDS ?? true} 
              onChange={e => setFormData(f => ({...f, includeTDS: e.target.checked}))} 
              disabled={isIssued}
              style={{ width: 16, height: 16, accentColor: TOKENS.navyDeep }}
            />
            Include TDS (10% &gt; 1L)
          </label>
        </div>
      </Card>

      {calculation && (
        <>
          <SectionLabel>Payslip Preview</SectionLabel>
          <Card style={{ padding: 20, marginBottom: 24, fontFamily: "sans-serif", background: "#fff", maxWidth: "800px", margin: "0 auto", border: "1px solid #ddd" }}>
            <div id="payslip-preview-content" style={{ padding: "10px", background: "#fff" }}>
            {/* Header */}
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: "bold" }}>SREYAS SOFTWARE SOLUTIONS PRIVATE LIMITED</h2>
              <div style={{ fontSize: 10, color: "#555", marginTop: 4 }}>Reg.Off : No.7/38, Rajaji Nagar, Dharga Road, Zamin Pallavaram, Chennai-600 043.</div>
              <div style={{ borderBottom: "2px solid #000", margin: "8px 0" }} />
              <h3 style={{ margin: 0, fontSize: 13, fontWeight: "bold" }}>Pay Slip for {monthStr}-{formData.year}</h3>
              <h2 style={{ margin: "6px 0 0 0", fontSize: 14 }}>{selectedEmp.name.toUpperCase()}</h2>
              <div style={{ borderBottom: "2px solid #000", margin: "8px 0" }} />
            </div>

            {/* Details Grid */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 20px", fontSize: 10, marginBottom: 16 }}>
              <div style={{ display: "flex" }}><span style={{ width: 140 }}>Employee Number</span>: {selectedEmp.employeeId || selectedEmp.employee_id || "N/A"}</div>
              <div style={{ display: "flex" }}><span style={{ width: 140 }}>Tax Regime</span>: Regular Tax Regime</div>
              <div style={{ display: "flex" }}><span style={{ width: 140 }}>Function</span>:</div>
              <div style={{ display: "flex" }}><span style={{ width: 140 }}>Income Tax Number (PAN)</span>: {selectedEmp.panNo || "N/A"}</div>
              <div style={{ display: "flex" }}><span style={{ width: 140 }}>Designation</span>: {selectedEmp.role || "N/A"}</div>
              <div style={{ display: "flex" }}><span style={{ width: 140 }}>PF account number</span>: {selectedEmp.pfNo || "N/A"}</div>
              <div style={{ display: "flex" }}><span style={{ width: 140 }}>Location</span>:</div>
              <div style={{ display: "flex" }}><span style={{ width: 140 }}>ESI Number</span>: {selectedEmp.esiNo || "N/A"}</div>
              <div style={{ display: "flex" }}><span style={{ width: 140 }}>Date of joining</span>: {selectedEmp.doj || "16-Apr-25"}</div>
              <div style={{ display: "flex" }}><span style={{ width: 140 }}>Bank Account No</span>:</div>
            </div>

            <div style={{ borderBottom: "1px solid #000", marginBottom: 16 }} />

            {/* Attendance Details Table */}
            <table style={{ width: "40%", borderCollapse: "collapse", fontSize: 10, marginBottom: 16 }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #000" }}>
                  <th style={{ textAlign: "left", padding: "4px 0" }}>Attendance Details</th>
                  <th style={{ textAlign: "right", padding: "4px 0" }}>Value</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: "1px solid #ddd" }}>
                  <td style={{ padding: "6px 0" }}>Total Days in Month</td>
                  <td style={{ textAlign: "right", padding: "6px 0" }}>{calculation.totalDaysInMonth} Days</td>
                </tr>
                <tr style={{ borderBottom: "1px solid #ddd" }}>
                  <td style={{ padding: "6px 0" }}>Paid Days</td>
                  <td style={{ textAlign: "right", padding: "6px 0" }}>{calculation.totalPaidDays} Days</td>
                </tr>
                <tr style={{ borderBottom: "1px solid #ddd" }}>
                  <td style={{ padding: "6px 0" }}>Absent (LOP)</td>
                  <td style={{ textAlign: "right", padding: "6px 0" }}>{calculation.lopDays} Days</td>
                </tr>
                <tr style={{ borderBottom: "1px solid #ddd" }}>
                  <td style={{ padding: "6px 0" }}>Casual Leave</td>
                  <td style={{ textAlign: "right", padding: "6px 0" }}>{formData.casualLeave || 0} Days</td>
                </tr>
              </tbody>
            </table>

            {/* Earnings & Deductions Table */}
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10, marginBottom: 20, borderTop: "2px solid #000", borderBottom: "2px solid #000" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #000" }}>
                  <th style={{ textAlign: "left", padding: "6px 4px" }}>Earnings</th>
                  <th style={{ textAlign: "right", padding: "6px 4px" }}>Amount</th>
                  <th style={{ textAlign: "right", padding: "6px 4px" }}>Gross Salary</th>
                  <th style={{ textAlign: "left", padding: "6px 4px", paddingLeft: 12 }}>Deductions</th>
                  <th style={{ textAlign: "right", padding: "6px 4px" }}>Amount</th>
                  <th style={{ textAlign: "right", padding: "6px 4px" }}>Gross Salary</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: "1px solid #ddd" }}>
                  <td style={{ padding: "6px 4px" }}>Fixed Salary</td>
                  <td style={{ textAlign: "right", padding: "6px 4px" }}>{calculation.proratedBasic.toFixed(2)}</td>
                  <td style={{ textAlign: "right", padding: "6px 4px" }}>{parseFloat(formData.basicPay).toFixed(2)}</td>
                  <td style={{ padding: "6px 4px", paddingLeft: 12 }}>LOP</td>
                  <td style={{ textAlign: "right", padding: "6px 4px" }}>{(parseFloat(formData.actualPay) - calculation.proratedActual).toFixed(2)}</td>
                  <td style={{ textAlign: "right", padding: "6px 4px" }}></td>
                </tr>
                <tr style={{ borderBottom: "1px solid #ddd" }}>
                  <td style={{ padding: "6px 4px" }}>Allowances</td>
                  <td style={{ textAlign: "right", padding: "6px 4px" }}>{calculation.allowances.toFixed(2)}</td>
                  <td style={{ textAlign: "right", padding: "6px 4px" }}>{(parseFloat(formData.actualPay) - parseFloat(formData.basicPay)).toFixed(2)}</td>
                  <td style={{ padding: "6px 4px", paddingLeft: 12 }}>PF Contribution</td>
                  <td style={{ textAlign: "right", padding: "6px 4px" }}>{calculation.pfEmployee.toFixed(2)}</td>
                  <td style={{ textAlign: "right", padding: "6px 4px" }}></td>
                </tr>
                <tr style={{ borderBottom: "1px solid #ddd" }}>
                  <td style={{ padding: "6px 4px" }}>Incentive / Bonus</td>
                  <td style={{ textAlign: "right", padding: "6px 4px" }}>{calculation.incentiveAmount > 0 ? calculation.incentiveAmount.toFixed(2) : ""}</td>
                  <td style={{ textAlign: "right", padding: "6px 4px" }}>{calculation.incentiveAmount > 0 ? calculation.incentiveAmount.toFixed(2) : ""}</td>
                  <td style={{ padding: "6px 4px", paddingLeft: 12 }}>ESI Contribution</td>
                  <td style={{ textAlign: "right", padding: "6px 4px" }}>{calculation.esiEmployee.toFixed(2)}</td>
                  <td style={{ textAlign: "right", padding: "6px 4px" }}></td>
                </tr>
                <tr style={{ borderBottom: "1px solid #ddd" }}>
                  <td style={{ padding: "6px 4px" }}></td>
                  <td style={{ textAlign: "right", padding: "6px 4px" }}></td>
                  <td style={{ textAlign: "right", padding: "6px 4px" }}></td>
                  <td style={{ padding: "6px 4px", paddingLeft: 12 }}>TDS</td>
                  <td style={{ textAlign: "right", padding: "6px 4px" }}>{calculation.tds.toFixed(2)}</td>
                  <td style={{ textAlign: "right", padding: "6px 4px" }}></td>
                </tr>
                <tr style={{ borderTop: "2px solid #000", fontWeight: "bold" }}>
                  <td style={{ padding: "6px 4px" }}>Total Earnings</td>
                  <td style={{ textAlign: "right", padding: "6px 4px" }}>{calculation.totalEarningsDisplay.toFixed(2)}</td>
                  <td style={{ textAlign: "right", padding: "6px 4px" }}>{(parseFloat(formData.actualPay) + calculation.incentiveAmount).toFixed(2)}</td>
                  <td style={{ padding: "6px 4px", paddingLeft: 12 }}>Total Deductions</td>
                  <td style={{ textAlign: "right", padding: "6px 4px" }}>{(calculation.totalDeductions + (parseFloat(formData.actualPay) - calculation.proratedActual)).toFixed(2)}</td>
                  <td style={{ textAlign: "right", padding: "6px 4px" }}></td>
                </tr>
                <tr style={{ borderTop: "2px solid #000", fontWeight: "bold" }}>
                  <td colSpan={3} style={{ padding: "6px 4px" }}></td>
                  <td style={{ padding: "6px 4px", paddingLeft: 12 }}>Net Amount</td>
                  <td style={{ textAlign: "right", padding: "6px 4px" }}>Rs {calculation.netPay.toFixed(2)}</td>
                  <td style={{ textAlign: "right", padding: "6px 4px" }}>Rs {(parseFloat(formData.actualPay) + calculation.incentiveAmount).toFixed(2)}</td>
                </tr>
              </tbody>
            </table>

            {/* Amount in words */}
            <div style={{ fontSize: 10, marginBottom: 30 }}>
              <div>Amount (in words):</div>
              <div style={{ fontWeight: "bold", marginTop: 4 }}>INR {numberToWords(Math.round(calculation.netPay))} Only</div>
            </div>

            {/* Footer */}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
              <div style={{ textAlign: "right", fontSize: 10 }}>
                <div style={{ fontWeight: "bold", marginBottom: 30 }}>for SREYAS SOFTWARE SOLUTIONS PRIVATE LIMITED</div>
                <div>Authorised Signatory</div>
              </div>
            </div>
            </div>
            
            {!isIssued ? (
              <button
                onClick={generatePDF}
                style={{
                  width: "100%", marginTop: 30, padding: "14px", borderRadius: 12,
                  background: TOKENS.navyDeep, color: TOKENS.gold, border: "none",
                  fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  cursor: "pointer", boxShadow: `0 4px 12px ${TOKENS.navyDeep}30`
                }}
              >
                <Download size={18} /> Approve & Issue Payslip
              </button>
            ) : (
              <div style={{ display: "flex", gap: 12, marginTop: 30 }}>
                <button
                  onClick={generatePDF}
                  style={{
                    flex: 1, padding: "14px", borderRadius: 12,
                    background: TOKENS.navyDeep, color: TOKENS.gold, border: "none",
                    fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    cursor: "pointer", boxShadow: `0 4px 12px ${TOKENS.navyDeep}30`
                  }}
                >
                  <Download size={18} /> Re-Download PDF
                </button>
                <button
                  onClick={() => setIsIssued(false)}
                  style={{
                    flex: 1, padding: "14px", borderRadius: 12,
                    background: "#f1f5f9", color: TOKENS.navyDeep, border: `1px solid ${TOKENS.border}`,
                    fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    cursor: "pointer"
                  }}
                >
                  <Edit size={18} /> Edit
                </button>
                <button
                  onClick={handleDeletePayslip}
                  style={{
                    flex: 1, padding: "14px", borderRadius: 12,
                    background: "#fee2e2", color: "#dc2626", border: "1px solid #fca5a5",
                    fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    cursor: "pointer"
                  }}
                >
                  <Trash2 size={18} /> Delete
                </button>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
